import React, { useState } from 'react';
import { Plus, X, GripVertical, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface OptionListEditorProps {
  options: string[];
  onChange: (options: string[]) => void;
  disabled?: boolean;
  error?: string;
}

export const OptionListEditor: React.FC<OptionListEditorProps> = ({
  options,
  onChange,
  disabled = false,
  error,
}) => {
  const [newOption, setNewOption] = useState('');
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  const handleAddOption = () => {
    const trimmed = newOption.trim();
    if (trimmed && !options.includes(trimmed)) {
      onChange([...options, trimmed]);
      setNewOption('');
    }
  };

  const handleRemoveOption = (index: number) => {
    const newOptions = options.filter((_, i) => i !== index);
    onChange(newOptions);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddOption();
    }
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newOptions = [...options];
    const draggedOption = newOptions[draggedIndex];
    newOptions.splice(draggedIndex, 1);
    newOptions.splice(index, 0, draggedOption);
    onChange(newOptions);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  return (
    <div className="space-y-3">
      {/* Options List */}
      <div className="space-y-2">
        {options.length === 0 ? (
          <div className="text-sm text-gray-500 italic py-2">
            No options added yet. Add at least one option below.
          </div>
        ) : (
          options.map((option, index) => (
            <div
              key={`${option}-${index}`}
              draggable={!disabled}
              onDragStart={() => handleDragStart(index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={handleDragEnd}
              className={cn(
                'flex items-center gap-2 p-2 bg-gray-50 rounded-lg group',
                draggedIndex === index && 'opacity-50',
                !disabled && 'cursor-move'
              )}
            >
              <GripVertical className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <span className="flex-1 text-sm text-gray-700">{option}</span>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => handleRemoveOption(index)}
                  className="p-1 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          ))
        )}
      </div>

      {/* Add New Option */}
      {!disabled && (
        <div className="flex items-center gap-2">
          <Input
            value={newOption}
            onChange={(e) => setNewOption(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Add new option..."
            className="flex-1"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAddOption}
            disabled={!newOption.trim() || options.includes(newOption.trim())}
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Duplicate Warning */}
      {newOption.trim() && options.includes(newOption.trim()) && (
        <div className="flex items-center gap-1 text-xs text-amber-600">
          <AlertCircle className="w-3 h-3" />
          This option already exists
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-1 text-xs text-red-600">
          <AlertCircle className="w-3 h-3" />
          {error}
        </div>
      )}
    </div>
  );
};
